import { useEffect, useRef, useState } from "react";
import { Mic, Square, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useApi, errMsg } from "@/lib/api";

const PLACEHOLDER = "es. \"Convegno per MODE Srl all'Hilton Sorrento il 9 febbraio, allestimento il giorno prima. Impianto RCF, mixer SQ5, 4 radiomicrofoni, ledwall 6x2,5, regia ATEM, gobbo, 3 tecnici\"";

function useRecorder(onAudio) {
  const [recording, setRecording] = useState(false);
  const recRef = useRef(null);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ["audio/webm", "audio/mp4"].find((t) => window.MediaRecorder?.isTypeSupported?.(t));
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = rec.mimeType || "audio/webm";
        onAudio(new Blob(chunks, { type }), type.includes("mp4") ? "mp4" : "webm");
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Microfono non disponibile: controlla i permessi del browser");
    }
  };
  const stop = () => {
    recRef.current?.stop();
    setRecording(false);
  };
  return { recording, start, stop };
}

export const AiComposer = ({ onText, initialText = "", autoRun = false, compact = false }) => {
  const api = useApi();
  const [text, setText] = useState(initialText);
  const [busy, setBusy] = useState(null);

  const submit = async (value) => {
    const t = (value ?? text).trim();
    if (t.length < 3) return toast.error("Descrivi l'evento in poche parole");
    setBusy("compose");
    try {
      await onText(t);
    } finally {
      setBusy(null);
    }
  };

  const { recording, start, stop } = useRecorder(async (blob, ext) => {
    setBusy("transcribe");
    try {
      const fd = new FormData();
      fd.append("file", blob, `voce.${ext}`);
      const r = await api.post("/ai/transcribe", fd);
      const combined = `${text ? text + " " : ""}${r.data.text}`.trim();
      setText(combined);
      await submit(combined);
    } catch (e) {
      toast.error(errMsg(e, "Trascrizione non riuscita"));
      setBusy(null);
    }
  });

  const autoRan = useRef(false);
  useEffect(() => {
    // guard: React StrictMode runs effects twice in dev, which composed (and added) the lines twice
    if (autoRan.current) return;
    autoRan.current = true;
    if (autoRun && initialText) submit(initialText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative rounded-lg border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.07] to-transparent p-4 sm:p-5" data-testid="ai-composer">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-amber-400" />
        <span className="micro-label text-amber-300">Composizione rapida AI · testo o voce</span>
      </div>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={PLACEHOLDER}
        rows={compact ? 2 : 3}
        className="bg-slate-950/60 border-slate-700 resize-none text-sm"
        data-testid="ai-prompt-input"
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
      />
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <Button
          type="button"
          variant={recording ? "destructive" : "secondary"}
          onClick={recording ? stop : start}
          disabled={busy !== null}
          className={recording ? "rec-pulse" : ""}
          data-testid="ai-mic-record-btn"
        >
          {recording ? <><Square className="h-4 w-4 mr-2" />Ferma e componi</> : <><Mic className="h-4 w-4 mr-2" />Detta a voce</>}
        </Button>
        <Button type="button" onClick={() => submit()} disabled={busy !== null || recording} data-testid="ai-generate-quote-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
          {busy === "transcribe" ? "Trascrivo..." : busy === "compose" ? "Compongo..." : "Componi con AI"}
        </Button>
        <span className="text-xs text-slate-500 hidden sm:inline">Ctrl+Invio per inviare. Le voci vengono aggiunte al preventivo.</span>
      </div>
    </div>
  );
};
